import React, { useContext, useMemo, useState } from 'react';
import { chain } from 'lodash';
import { SButton } from '@chargebee/sting-react';
import { PivotTableContext } from '@bbkAdminComponents/pivot-table/pivot-table-context';
import {
  GenericPopover,
  type ModalOnCloseReason,
} from '@bbkAdminComponents/popovers/generic-popover';
import { Separator } from '@bbkAdminComponents/reports-filter-picker/reports-filter-picker';
import { GenericSelect } from '@bbkAdminComponents/select/select';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { Icons } from '@bbkAdminUtils/icons';

export const PivotTableFilter: React.FC<{
  opts: { value: string | undefined; label: string }[];
}> = ({ opts }) => {
  const { groupByOpts: initGroupByOpts, setGroupByOpts: initSetGroupByOpts } =
    useContext(PivotTableContext);

  const [groupByOpts, setGroupByOpts] = useState(initGroupByOpts);

  const actionButton = useMemo(
    () => (props: any) => (
      <button
        className="timeAgoButton !tw-flex tw-flex-row tw-items-center"
        type="button"
        {...props}
      >
        <span className="tw-mr-3">{Icons.funnel}</span>
        <span className="tw-font-semibold tw-text-black">
          {initGroupByOpts
            .filter((s) => s)
            .map((s) => opts.find((opt) => opt.value === s)?.label)
            .filter(isDefined)
            .join(' > ') || 'None'}
        </span>
      </button>
    ),
    [initGroupByOpts]
  );

  const handlePopoverClose = (reason?: ModalOnCloseReason) => {
    if (reason) {
      setGroupByOpts(initGroupByOpts);
    }
  };

  const getLabel = (opt: unknown) => {
    const { icon, label } = opt as {
      icon: React.ReactNode;
      label: React.ReactNode;
    };
    return (
      <div className="tw-flex tw-items-center">
        <span className="tw-mr-1">{icon}</span>
        <span>{label}</span>
      </div>
    );
  };

  return (
    <GenericPopover
      popoverProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          style: { width: '20rem', overflow: 'initial' },
        },
        classes: { paper: 'bbk-generic-dialog-popover' },
        disableEnforceFocus: true,
      }}
      onClose={handlePopoverClose}
      actionButton={actionButton}
    >
      {({ handleClose }) => {
        const handleSave = () => {
          initSetGroupByOpts(
            chain(groupByOpts).filter(isDefined).uniq().value()
          );
          handleClose();
        };

        return (
          <div className="[&_svg]:tw-fill-black/50">
            <div className="bbk-gdialog-header tw-px-5 tw-py-4">
              <div className="bbk-gdialog-title">Grouping</div>
              <div className="bbk-gdialog-header-actions">
                <SButton
                  variant="primary"
                  className="bbk-gdialog-button"
                  onClick={handleSave}
                >
                  Apply
                </SButton>
              </div>
            </div>
            <Separator />
            <div className="tw-my-3 tw-px-6">
              <div className="tw-mb-2 tw-text-xs tw-text-primary-600">
                First group
              </div>

              <GenericSelect
                formatOptionLabel={getLabel}
                isSearchable={false}
                short
                options={opts}
                placeholder="Select group..."
                onChange={({ value }) =>
                  setGroupByOpts([
                    ...groupByOpts.slice(0, 0),
                    value,
                    ...groupByOpts.slice(1),
                  ])
                }
                value={opts.find((opt) => opt.value === groupByOpts[0])}
              />
            </div>
            <div className="tw-my-3 tw-px-6">
              <div className="tw-mb-2 tw-text-xs tw-text-primary-600">
                Second group
              </div>

              <GenericSelect
                formatOptionLabel={getLabel}
                isSearchable={false}
                short
                options={opts}
                placeholder="Select group..."
                onChange={({ value }) => {
                  const optsBefore = groupByOpts.slice(0, 1);
                  const optsAfter = groupByOpts.slice(2);
                  setGroupByOpts([...optsBefore, value, ...optsAfter]);
                }}
                value={opts.find((opt) => opt.value === groupByOpts[1])}
              />
            </div>
            <div className="tw-my-3 tw-mb-6 tw-px-6">
              <div className="tw-mb-2 tw-text-xs tw-text-primary-600">
                Third group
              </div>

              <GenericSelect
                formatOptionLabel={getLabel}
                isSearchable={false}
                short
                options={opts}
                onChange={({ value }) =>
                  setGroupByOpts([
                    ...groupByOpts.slice(0, 2),
                    value,
                    ...groupByOpts.slice(3),
                  ])
                }
                value={opts.find((opt) => opt.value === groupByOpts[2])}
                placeholder="None"
              />
            </div>
          </div>
        );
      }}
    </GenericPopover>
  );
};
